/* eslint-disable @typescript-eslint/no-use-before-define */
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { PredefinedAnnotationTags } from '@euler/app/flows/media/annotation/functions/tagPresets';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import {
  compareOption,
  customIssueStagedToItemStaged,
  inspectedItemStagedToCustomIssueStaged,
  isMediaStagedPending,
  mediaStagedToMediaInfo,
} from '@euler/app/flows/task/functions/task-detail/util';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import {
  defectiveLevelDistributed,
  getDefectiveLevel,
} from '@euler/functions/getDefectiveLevel';
import {
  isSimpleNumericInspectionResultDataType,
  isSimpleNumericItemResult,
} from '@euler/functions/inspectionResultDataTypeHelpers';
import { useHasProducts } from '@euler/functions/product/useHasProducts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import {
  DefectiveLevel,
  InspectionItemOptionInfo,
  InspectionSiteInfo,
} from '@euler/model';
import {
  AbnormalLevel,
  ImageAnnotationScene,
  InspectionCategory,
  SiteInspectionType,
  TaskProductManifestItemOrigin,
} from '@euler/model/enum';
import {
  CustomIssueItemStaged,
  TaskProductInfo,
  TaskProductManifestItemStaged,
} from '@euler/model/task-detail';
import { delayed, isNotNull, showAlert, withConfirmation } from '@euler/utils';
import { sorted } from '@euler/utils/array';
import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
  usePersistFn,
} from '@euler/utils/hooks';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import {
  StackNavigationOptions,
  StackNavigationProp,
} from '@react-navigation/stack';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTheme } from 'styled-components';
import { useSiteInspectionNavigatorContext } from './SiteInspectionContext';
import {
  SiteInspectionNavParams,
  useSiteInspectionWillDismissListener,
} from './SiteInspectionNavigator';
import { ScrollViewContainer } from './components/ScrollViewContainer';
import { SiteInspectionView } from './components/SiteInspectionView';
import { CheckItemInfo, OptionInfo } from './functions/SiteInspection';
import { DismissEvent } from './types';

export const SiteInspectionScreen = wrapNavigatorScreen(
  ({
    site,
    flowId,
    mediaCaptureRequired,
    onDone,
  }: {
    flowId: string;
    site: InspectionSiteInfo;
    mediaCaptureRequired?: boolean;
    onDone?: () => void;
  }) => {
    useToggleAutomaticKeyboard(true);

    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const isLandscape = useIsLandscape();
    const [hasProducts] = useHasProducts();

    const appNavigation = useAppNavigation();

    const navigation =
      useNavigation<StackNavigationProp<SiteInspectionNavParams>>();

    const productManifestBucketRef = useRef(`site-inspection/${site.id}`);

    const { preventNavigateAway$, bottomComponent$, navigatorContainerType } =
      useSiteInspectionNavigatorContext();

    const {
      taskManager,
      detail: {
        basicInfo: { licensePlateNo },
      },
    } = useTaskContext();

    const { user, store } = useCurrentUser()!;

    const siteInspectionManager =
      taskManager.inspectionManager.beginSiteInspection({
        flowId,
        site,
        inspectionType: SiteInspectionType.Default,
        technicianId: user.memberId,
        technicianName: user.name,
      });

    const [inspection] = useBehaviorSubject(siteInspectionManager.staged$);
    const isDirty = useRef(false);

    useEffect(() => {
      const sub = siteInspectionManager.dirty$.subscribe(dirty => {
        isDirty.current = dirty;
        preventNavigateAway$.next(dirty);
      });
      return () => sub.unsubscribe();
    }, [preventNavigateAway$, siteInspectionManager.dirty$]);

    const onWillDismiss = usePersistFn(async (e: DismissEvent) => {
      if (!isDirty.current) return;

      e.preventDefault();
      const confirmed = await withConfirmation({
        title: '确认',
        message: '返回后您所填写的信息将丢失，是否继续?',
        cancelButtonText: '否',
        confirmButtonText: '是',
      });
      if (confirmed) {
        taskManager.productManifestManager.removeProductsOfBucket(
          productManifestBucketRef.current,
        );
        e.dismiss();
      }
    });

    useSiteInspectionWillDismissListener(onWillDismiss);

    const navigateToCustomIssueScreen = useCallback(
      (customIssue?: CustomIssueItemStaged) => {
        navigation.navigate('_customIssue', {
          category: InspectionCategory.Normal,
          inspectionType: SiteInspectionType.Default,
          flowId,
          siteId: site.id,
          title: site.name + '\n自定义故障',
          showMedias: true,
          customIssue,
          onDone: customIssueInspectionManager => {
            const customIssueStaged =
              customIssueInspectionManager.staged$.getValue();
            const itemStaged = customIssueStagedToItemStaged(
              customIssueStaged,
              user.memberId,
              user.name,
            );
            siteInspectionManager.produce(draft => {
              const index = draft.items.findIndex(
                x => x._key === itemStaged._key,
              );
              if (index < 0) {
                draft.items.push(itemStaged);
              } else {
                draft.items[index] = itemStaged;
              }
            });
            navigation.goBack();
          },
        });
      },
      [
        flowId,
        navigation,
        site.id,
        site.name,
        siteInspectionManager,
        user.memberId,
        user.name,
      ],
    );

    const onCustomIssueButtonPress = useCallback(() => {
      navigateToCustomIssueScreen();
    }, [navigateToCustomIssueScreen]);

    const HeaderRight = useCallback(() => {
      return (
        <View
          css={`
            flex: 1;
            align-self: flex-end;
            margin-right: ${rpx(15)}px;
            justify-content: flex-end;
            flex-direction: row;
            align-items: center;
          `}
        >
          <LinkButton
            text="自定义故障"
            icon={
              <Icon
                name="add"
                size={rpx(
                  navigatorContainerType === 'sidepanel' && isLandscape
                    ? 21
                    : 16,
                )}
                color={theme.link}
              />
            }
            textStyle={{
              fontFamily: FontFamily.NotoSans.Light,
              fontSize: rpx(
                navigatorContainerType === 'sidepanel' && isLandscape ? 18 : 14,
              ),
            }}
            onPress={onCustomIssueButtonPress}
          />
        </View>
      );
    }, [
      isLandscape,
      navigatorContainerType,
      onCustomIssueButtonPress,
      rpx,
      theme.link,
    ]);

    useEffect(() => {
      navigation.setOptions({
        headerRight: HeaderRight,
      });
    }, [HeaderRight, navigation]);

    const inspectedItems = useMemo(() => {
      const list = inspection.items.map<CheckItemInfo | null>(inspectedItem => {
        if (inspectedItem.type === 'customIssue') {
          return {
            key: inspectedItem._key,
            type: 'customIssue',
            id: inspectedItem.id,
            name: inspectedItem.name,
            customIssueId: inspectedItem.id,
            options: [
              {
                defectiveLevel: getDefectiveLevel(
                  inspectedItem.abnormalLevel,
                  inspectedItem.severityLevel,
                ),
                isCustom: false,
                title: inspectedItem.inspectionResult,
                maintenanceAdvice: inspectedItem.maintenanceAdvice,
              },
            ],
            selectedOptionIndex: 0,
            medias: inspectedItem?.medias?.map(mediaStagedToMediaInfo),
            relatedFaultLikeSubjects: inspectedItem.relatedFaultLikeSubjects,
          };
        }

        const checkItem = site.checkItems.find(
          x => x.id === inspectedItem.itemId,
        );
        if (!checkItem) return null;

        const checkOptions = sorted(checkItem.options, compareOption);
        let selectedOptionIndex = -1;

        const overriddenDefectiveLevel = inspectedItem
          ? getDefectiveLevel(
              inspectedItem.abnormalLevel,
              inspectedItem.severityLevel,
            )
          : undefined;

        if (inspectedItem.isCustom) {
          selectedOptionIndex =
            inspectedItem.customLabelOptionIndex != null
              ? inspectedItem.customLabelOptionIndex
              : -1;
        } else {
          const candidates = checkOptions.filter(
            x =>
              x.abnormalLevel === inspectedItem.abnormalLevel &&
              x.severityLevel === inspectedItem.severityLevel,
          );
          if (candidates.length === 1) {
            selectedOptionIndex = checkOptions.findIndex(
              x =>
                x.abnormalLevel === inspectedItem.abnormalLevel &&
                x.severityLevel === inspectedItem.severityLevel,
            );
          } else if (candidates.length > 1) {
            selectedOptionIndex = checkOptions.findIndex(
              x =>
                x.abnormalLevel === inspectedItem.abnormalLevel &&
                x.severityLevel === inspectedItem.severityLevel &&
                x.label === inspectedItem.optionLabel,
            );
          } else {
            selectedOptionIndex = -1;
          }
        }

        if (selectedOptionIndex === -1) {
          // select by option label first.
          const index = checkOptions.findIndex(
            x => x.label === inspectedItem.label,
          );

          if (index >= 0) {
            selectedOptionIndex = index;
          }
        }

        const itemInfo: CheckItemInfo = {
          type: 'item',
          id: inspectedItem.id,
          key: inspectedItem._key,
          name: checkItem.name,
          itemId: checkItem.id,
          value: isSimpleNumericItemResult(inspectedItem.result)
            ? inspectedItem.result.value
            : undefined,
          valueType: checkItem.valueType ?? undefined,
          valueUnit: checkItem.valueUnit ?? undefined,
          protocolFieldId: checkItem.protocolFieldId ?? undefined,
          positionCode: checkItem.positionCode ?? undefined,
          isPicPreferred: checkItem.isPicPreferred ?? undefined,
          isCustom: inspectedItem.isCustom,
          options: checkOptions.map<OptionInfo>((option, i) => {
            const isCustom =
              inspectedItem.isCustom &&
              ((inspectedItem.abnormalLevel === option.abnormalLevel &&
                inspectedItem.severityLevel === option.severityLevel &&
                inspectedItem.optionLabel === option.label) ||
                inspectedItem.customLabelOptionIndex === i);
            const defectiveLevel = getDefectiveLevel(
              option.abnormalLevel,
              option.severityLevel,
            );
            return {
              isPicPreferred: option.isPicPreferred,
              defectiveLevel: isCustom
                ? overriddenDefectiveLevel ?? defectiveLevel
                : defectiveLevel,
              isCustom,
              title: isCustom
                ? inspectedItem.label ?? option.label
                : option.label,
              maintenanceAdvice:
                (isCustom
                  ? inspectedItem.maintenanceAdvice ?? option.maintenanceAdvice
                  : option.maintenanceAdvice) ?? undefined,
            };
          }),
          selectedOptionIndex,
          medias: inspectedItem?.medias?.map(mediaStagedToMediaInfo),
          relatedFaultLikeSubjects: inspectedItem.relatedFaultLikeSubjects,
        };

        if (selectedOptionIndex === -1 && inspectedItem.label) {
          // add extra option for missing match
          selectedOptionIndex = itemInfo.options.length;
          itemInfo.options.push({
            defectiveLevel: getDefectiveLevel(
              inspectedItem.abnormalLevel,
              inspectedItem.severityLevel,
            ),
            isCustom: true,
            title: inspectedItem.label,
            maintenanceAdvice: inspectedItem.maintenanceAdvice,
          });
        }

        return itemInfo;
      });

      return list.filter(isNotNull);
    }, [inspection.items, site.checkItems]);

    const onToggleRelatedFaultLikeSubject = useCallback(
      (item: CheckItemInfo, name: string) => {
        siteInspectionManager.produceItem(item.key, draft => {
          if (draft.relatedFaultLikeSubjects == null) {
            return;
          }
          const subject = draft.relatedFaultLikeSubjects.find(
            x => x.name === name,
          );
          if (subject != null) {
            subject.checked = !subject.checked;
          }
        });
      },
      [siteInspectionManager],
    );

    const onSelectedOptionChange = useCallback(
      (item: CheckItemInfo, index: number) => {
        if (!item.itemId) return;
        const target = site.checkItems.find(x => x.id === item.itemId);
        if (!target) return;
        const selectedOption = item.options[index];
        if (!selectedOption) return;
        const options = sorted(target.options, compareOption);
        const option = options[index];
        siteInspectionManager.produceItem(item.key, draft => {
          if (draft.type !== 'item') return;
          draft.abnormalLevel = option.abnormalLevel;
          draft.severityLevel = option.severityLevel;
          if (!selectedOption.isCustom) {
            // reset the label for different option.
            draft.label = undefined;
            draft.maintenanceAdvice = undefined;
            draft.customLabelOptionIndex = undefined;
            draft.isCustom = false;
            draft.optionLabel = option.label;
            draft.optionLabelFormat = option.labelFormat ?? undefined;
          }
        });
      },
      [site.checkItems, siteInspectionManager],
    );

    const watermark = useCallback(
      (scale?: number) => (
        <TaskImageWatermark
          licensePlateNo={licensePlateNo}
          title={site.name}
          author={user.name}
          store={store.name}
          type="inspection"
          scale={scale}
        />
      ),
      [licensePlateNo, site.name, store.name, user.name],
    );

    const takeMedia = useMediaCaptureWithAnnotation({
      scene: ImageAnnotationScene.Inspection,
      mediaCaptureRequired,
      maxMediaCountToSelect: 100,
    });

    const onTakeMedia = usePersistFn((item: CheckItemInfo) => {
      const option = item.options[item.selectedOptionIndex];
      takeMedia({
        tags: () => [
          ...new Set([
            ...item.options.map(x => x.title),
            ...PredefinedAnnotationTags.inspection,
          ]),
        ],
        defaultTag: () => ({
          name: option?.title,
          color:
            option.defectiveLevel === DefectiveLevel.Urgent
              ? 'red'
              : option.defectiveLevel === DefectiveLevel.Warning
              ? 'warning'
              : option.defectiveLevel === DefectiveLevel.Defective
              ? 'yellow'
              : 'green',
        }),
        watermark,
        uploadVideoFile: async (localFileUri, contentType, rotate) => {
          taskManager.uploadController.enqueue({
            localFileUri,
            contentType,
            type: 'video',
            parameters: {
              cover: true,
              blurhash: true,
              videoRotate: rotate,
            },
            context: {
              type: 'item-inspection',
              siteId: site.id,
              key: item.key,
              siteInspectionManagerId: siteInspectionManager.id,
            },
          });
        },
        handleImageAnnotationResults: async results => {
          results.forEach(result => {
            taskManager.uploadController.enqueue({
              localFileUri: result.imageUri,
              contentType: 'image/jpeg',
              type: 'image',
              // simulateFailure: true,
              parameters: {
                cover: true,
                blurhash: true,
              },
              context: {
                type: 'item-inspection',
                siteId: site.id,
                key: item.key,
                siteInspectionManagerId: siteInspectionManager.id,
                annotationMetadata: result.annotationMetadata,
              },
              listener: (e, task) => {
                if (e.type === 'finished') {
                  // upload the original image.
                  const media = siteInspectionManager.findMedia(
                    item.key,
                    task.key,
                  );

                  if (!media) return;

                  if (media.annotationMetadata?.origin_uri) return;

                  if (result.originalImageUri != null) {
                    taskManager.largeMediaUploadController.enqueue({
                      localFileUri: result.originalImageUri,
                      contentType: 'image/jpeg',
                      type: 'image',
                      parameters: {
                        blurhash: true,
                      },
                      context: {
                        type: 'custom',
                        tag: 'site-inspection/original-image',
                      },
                      onSuccess: ({ result: { url } }) => {
                        siteInspectionManager.produceItem(
                          item.key,
                          (draft, original) => {
                            const index = original.medias.findIndex(
                              x => x._key === media._key,
                            );
                            const metadata =
                              draft.medias[index]?.annotationMetadata;
                            if (metadata) {
                              metadata.origin_uri = url;
                            }
                          },
                          {
                            debugLabel: 'site-inspection/upload-original-image',
                            ignoreKeyError: true,
                          },
                        );
                      },
                    });
                  }
                }
              },
            });
          });
        },
      });
    });

    const onDeleteMedia = useCallback(
      async (item: CheckItemInfo, media: MediaInfo) => {
        if (
          await withConfirmation({
            title: '确认',
            message: '是否确认移除该图片?',
          })
        ) {
          siteInspectionManager.removeMedia(item.key, media.key);
        }
      },
      [siteInspectionManager],
    );

    const onEditMedia = usePersistFn(
      (item: CheckItemInfo, mediaInfo: MediaInfo) => {
        const mediaStaged = siteInspectionManager.findMedia(
          item.key,
          mediaInfo.key,
        );
        if (!mediaStaged) return;
        const originalImageUri = mediaStaged.annotationMetadata?.origin_uri;
        appNavigation.navigate('_imageAnnotation', {
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          imageUri: originalImageUri || mediaInfo.url,
          tags: [
            ...new Set([
              ...item.options.map(x => x.title),
              ...PredefinedAnnotationTags.inspection,
            ]),
          ],
          annotationMetadata: originalImageUri
            ? mediaStaged.annotationMetadata
            : undefined,
          watermark,
          onDone: results => {
            if (
              JSON.stringify(mediaStaged.annotationMetadata ?? null) ===
              JSON.stringify(results.annotationMetadata ?? null)
            ) {
              return;
            }
            const context = {
              type: 'item-inspection',
              siteId: site.id,
              key: item.key,
              mediaKey: mediaInfo.key,
              siteInspectionManagerId: siteInspectionManager.id,
              annotationMetadata: results.annotationMetadata,
            } as const;
            taskManager.uploadController.enqueue({
              localFileUri: results.imageUri,
              contentType: 'image/jpeg',
              type: 'image',
              // simulateFailure: true,
              parameters: {
                cover: true,
                blurhash: true,
              },
              context,
              listener: e => {
                if (e.type === 'finished') {
                  if (mediaStaged.annotationMetadata?.origin_uri) return;

                  // use the non-annotated media as the original url.
                  if (mediaStaged.annotationMetadata == null) {
                    siteInspectionManager.produceItem(
                      item.key,
                      (draft, original) => {
                        const index = original.medias.findIndex(
                          x => x._key === mediaStaged._key,
                        );
                        const metadata =
                          draft.medias[index]?.annotationMetadata;
                        if (metadata) {
                          metadata.origin_uri = mediaInfo.url;
                        }
                      },
                      { ignoreKeyError: true },
                    );
                  }
                }
              },
            });
            appNavigation.pop();
          },
        });
      },
    );

    const onRetryMediaUpload = useCallback(
      (item: CheckItemInfo, media: MediaInfo) => {
        if (media.source?.type !== 'local') return;
        taskManager.uploadController.enqueue({
          localFileUri: media.source.localFileUri,
          contentType: media.source.contentType,
          type: media.type,
          parameters: media.source.uploadParameters,
          context: {
            type: 'item-inspection',
            siteId: site.id,
            key: item.key,
            // add this media key to indicate retry instead of add new media object
            mediaKey: media.key,
            siteInspectionManagerId: siteInspectionManager.id,
          },
        });
      },
      [site.id, siteInspectionManager.id, taskManager.uploadController],
    );

    const onPreviewVideo = useCallback(
      (item: CheckItemInfo, media: MediaInfo) => {
        const stagedMedia = siteInspectionManager.findMedia(
          item.key,
          media.key,
        );
        if (
          stagedMedia &&
          stagedMedia._source.type === 'local' &&
          stagedMedia._source.state.status === 'uploaded'
        ) {
          appNavigation.navigate('_mediaPreview', {
            type: 'video',
            url: stagedMedia._source.localFileUri,
          });
        } else {
          appNavigation.navigate('_mediaPreview', {
            type: 'video',
            url: media.url,
          });
        }
      },
      [appNavigation, siteInspectionManager],
    );

    const appLoading = useAppLoading();
    const onSubmit = usePersistFn(async () => {
      let hasPendingMediaUpload = false;
      let requirePhoto = false;
      let hasMedia = false;

      siteInspectionManager.enumerate({
        onItem: item => {
          const targetItem = inspectedItems.find(x => x.key === item._key);
          if (
            targetItem?.options[targetItem.selectedOptionIndex]?.isPicPreferred
          ) {
            requirePhoto = true;
          }
          return true;
        },
        onMedia: media => {
          if (isMediaStagedPending(media)) {
            hasPendingMediaUpload = true;
          }
          hasMedia = true;
          return true;
        },
      });

      if (requirePhoto && !hasMedia) {
        void showAlert(
          '提示',
          `部分检测项目需要拍照，请上传至少一张图片或视频!`,
        );
        return;
      }

      if (hasPendingMediaUpload) {
        void showAlert('提示', '尚有图片未完成上传或已上传失败，请稍候重试!');
        return;
      }

      try {
        appLoading.show();

        let shouldFinish = true;

        try {
          await taskManager.productManifestManager.save({
            origin: {
              type: TaskProductManifestItemOrigin.Inspection,
            },
          });
        } catch (e) {
          console.error(e);
          alert('保存产品推荐清单失败，请稍后重试!');
          shouldFinish = false;
        }

        if (shouldFinish) {
          await siteInspectionManager.commit();
          appLoading.hide();
          if (navigatorContainerType !== 'sidepanel') {
            taskManager.inspectionManager.endSiteInspection(site.id);
          }
          onDone?.();
        } else {
          appLoading.hide();
        }
      } catch (e) {
        appLoading.hide();
        void showAlert('出错了', (e as Error).message);
      }
    });

    const setBottomComponent = useBehaviorSubjectUpdater(bottomComponent$);
    const footer = useMemo(
      () => (
        <BottomAction containerStyle={{ paddingBottom: 0 }}>
          <BottomAction.Button text="提交数据" onPress={onSubmit} />
        </BottomAction>
      ),
      [onSubmit],
    );

    const focused = useIsFocused();

    useEffect(() => {
      if (focused) {
        delayed(() => {
          setBottomComponent(footer);
        }, 200);
      } else {
        setBottomComponent(undefined);
      }
    }, [focused, footer, setBottomComponent]);

    const onItemValueChange = useCallback(
      (item: CheckItemInfo, value: number | undefined) => {
        siteInspectionManager.produceItem(item.key, draft => {
          if (draft.type === 'customIssue') return;
          if (!isSimpleNumericItemResult(draft.result)) return;
          draft.result.value = value;

          const checkItem = site.checkItems.find(x => x.id === item.itemId);

          if (
            null == checkItem ||
            !isSimpleNumericInspectionResultDataType(checkItem.valueType)
          ) {
            return;
          }

          const updateWithTargetOption = (
            targetOption: InspectionItemOptionInfo,
          ) => {
            draft.abnormalLevel = targetOption.abnormalLevel;
            draft.severityLevel = targetOption.severityLevel;
            // reset the label for different option.
            draft.label = undefined;
            draft.maintenanceAdvice = undefined;
            draft.customLabelOptionIndex = undefined;
            draft.isCustom = false;
            draft.optionLabel = targetOption.label;
            draft.optionLabelFormat = targetOption.labelFormat ?? undefined;
          };

          if (value == null) {
            // set the default normal option
            const normalOption = checkItem.options.find(
              x => x.abnormalLevel === AbnormalLevel.Fine,
            );
            if (normalOption != null) {
              updateWithTargetOption(normalOption);
            }
            return;
          }

          const matchLower = (lower: number, lowerInclusive?: boolean) => {
            return (
              (lowerInclusive && value >= lower) ||
              (!lowerInclusive && value > lower)
            );
          };
          const matchUpper = (upper: number, upperInclusive?: boolean) => {
            return (
              (upperInclusive && value <= upper) ||
              (!upperInclusive && value < upper)
            );
          };

          const foundOption = checkItem.options.find(
            option =>
              (option.lower != null &&
                option.upper != null &&
                matchLower(option.lower, option.lowerInclusive) &&
                matchUpper(option.upper, option.upperInclusive)) ||
              (option.lower != null &&
                option.upper == null &&
                matchLower(option.lower, option.lowerInclusive)) ||
              (option.lower == null &&
                option.upper != null &&
                matchUpper(option.upper, option.upperInclusive)),
          );
          if (foundOption != null) {
            updateWithTargetOption(foundOption);
          }
        });
      },
      [site.checkItems, siteInspectionManager],
    );

    const onEditMaintenanceAdvice = useCallback(
      (itemInfo: CheckItemInfo) => {
        const item = inspection.items.find(x => x._key === itemInfo.key);
        if (!item) return;
        if (item.type === 'customIssue') {
          const customIssueStaged =
            inspectedItemStagedToCustomIssueStaged(item);
          if (!customIssueStaged) return;
          navigateToCustomIssueScreen(customIssueStaged);
        } else {
          const checkItem = site.checkItems.find(x => x.id === itemInfo.itemId);
          if (!checkItem) return;
          const checkOptions = sorted(checkItem.options, compareOption);
          const checkOption = checkOptions[itemInfo.selectedOptionIndex];
          if (!checkOption) return;
          const option = itemInfo.options[itemInfo.selectedOptionIndex];
          if (!option) return;
          navigation.navigate('_customDefectiveResult', {
            title: item.name,
            label: option.title,
            optionLabel: checkOption.label,
            abnormalLevel: item.abnormalLevel,
            severityLevel: item.severityLevel,
            description: item.description ?? item.remark,
            maintenanceAdvice:
              item.maintenanceAdvice ?? checkOption.maintenanceAdvice ?? '',
            onDone: values => {
              siteInspectionManager.produceItem(item._key, draft => {
                if (draft.type !== 'item') return;
                const label =
                  !values.label || values.label === checkOption.label
                    ? undefined
                    : values.label;
                const remark = values.description;
                let isCustomDefectiveLevel = false;
                if (values.defectiveLevel != null) {
                  const { abnormalLevel, severityLevel } =
                    defectiveLevelDistributed(values.defectiveLevel);
                  if (
                    checkOption.abnormalLevel !== abnormalLevel ||
                    checkOption.severityLevel !== severityLevel
                  ) {
                    draft.abnormalLevel = abnormalLevel;
                    draft.severityLevel = severityLevel;
                    isCustomDefectiveLevel = true;
                  } else {
                    draft.abnormalLevel = checkOption.abnormalLevel;
                    draft.severityLevel = checkOption.severityLevel;
                  }
                }
                draft.label = label;
                draft.maintenanceAdvice = values.maintenanceAdvice;
                draft.remark = remark;
                if (
                  draft.label ||
                  values.maintenanceAdvice !== checkOption.maintenanceAdvice ||
                  isCustomDefectiveLevel
                ) {
                  draft.isCustom = true;
                  draft.customLabelOptionIndex = itemInfo.selectedOptionIndex;
                } else {
                  draft.isCustom = false;
                  draft.customLabelOptionIndex = undefined;
                }
              });
              navigation.goBack();
            },
          });
        }
      },
      [
        inspection.items,
        navigateToCustomIssueScreen,
        navigation,
        site.checkItems,
        siteInspectionManager,
      ],
    );

    const onAssociateProduct = useCallback(
      (subject: string) => {
        navigation.push('_addProduct', {
          subject,
          onGetManifest$: () => {
            return taskManager.productManifestManager.manifest$;
          },
          onDone: selection => {
            const products = selection.map<TaskProductInfo>(x => ({
              id: x.id,
              name: x.name,
              coverUrl: x.coverUrl,
              title: x.title ?? undefined,
              brandId: x.brandId,
              brandName: x.brandName,
              agentId: x.agentId,
              agentName: x.agentName,
              sourceContractId: x.sourceContractId,
            }));
            let changes = 0;
            for (const product of products) {
              const res =
                taskManager.productManifestManager.addProductForSubject(
                  subject,
                  product,
                  TaskProductManifestItemOrigin.Inspection,
                  productManifestBucketRef.current,
                );
              changes += res ? 1 : 0;
            }
            if (changes > 0) {
              siteInspectionManager.dirty$.next(true);
            }
            navigation.goBack();
          },
        });
      },
      [
        navigation,
        siteInspectionManager.dirty$,
        taskManager.productManifestManager,
      ],
    );

    const onDeleteItem = useCallback(
      async (itemInfo: CheckItemInfo) => {
        const index = inspection.items.findIndex(x => x._key === itemInfo.key);
        if (index < 0) return;
        const confirmed = await withConfirmation({
          title: '确认',
          message: '是否确认移除该自定义故障?',
        });
        if (!confirmed) return;
        siteInspectionManager.produce(draft => {
          draft.items.splice(index, 1);
        });
      },
      [inspection.items, siteInspectionManager],
    );

    const onCancel = usePersistFn(async () => {
      if (
        !(await withConfirmation({
          title: '确认',
          message: '您即将取消当前部位的检测结果，该操作不可撤销，是否继续？',
          confirmButtonText: '是',
          cancelButtonText: '否',
        }))
      ) {
        return;
      }
      await appLoading.showWithAction(async () => {
        await taskManager.inspectionManager.cancelSiteInspection(site.id);
        isDirty.current = false;
        onDone?.();
      });
    });

    useEffect(
      () => () => taskManager.inspectionManager.endSiteInspection(site.id),
      [site.id, taskManager.inspectionManager],
    );

    const [productManifest] = useBehaviorSubject(
      taskManager.productManifestManager.staged$,
    );

    const productManifestItems = useMemo(
      () => productManifest.items.filter(x => !x.removed),
      [productManifest],
    );

    const onRemoveProductManifestItem = usePersistFn(
      async (item: TaskProductManifestItemStaged) => {
        if (
          await withConfirmation({
            title: '确认',
            message: '是否确认移除该产品推荐? ',
          })
        ) {
          taskManager.productManifestManager.removeProductForSubject(
            item.subject,
            item.product.id,
          );
        }
      },
    );

    const onPressProductManifestItem = usePersistFn(
      async (item: TaskProductManifestItemStaged) => {
        navigation.push('_productDetail', {
          id: item.product.id,
        });
      },
    );

    return (
      <ScrollViewContainer
        bottomSpacing={
          navigatorContainerType === 'bottomsheet' ? undefined : rpx(100)
        }
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <SiteInspectionView
          items={inspectedItems}
          canAssociateProduct={hasProducts}
          productManifestItems={productManifestItems}
          onRemoveProductManifestItem={onRemoveProductManifestItem}
          onPressProductManifestItem={onPressProductManifestItem}
          onSelectedOptionChange={onSelectedOptionChange}
          onTakeMedia={onTakeMedia}
          onDeleteMedia={onDeleteMedia}
          onEditMedia={onEditMedia}
          onRetryMediaUpload={onRetryMediaUpload}
          onPreviewVideo={onPreviewVideo}
          onValueChange={onItemValueChange}
          onEditMaintenanceAdvice={onEditMaintenanceAdvice}
          onAssociateProduct={onAssociateProduct}
          onDelete={onDeleteItem}
          onToggleRelatedFaultLikeSubject={onToggleRelatedFaultLikeSubject}
        />
        {inspection.id != null ? (
          <TouchableOpacity
            css={`
              margin: ${rpx(15)}px;
              border-width: 1px;
              border-color: ${StatusColors.Danger};
              padding: ${rpx(8)}px;
              border-radius: ${rpx(6)}px;
            `}
            onPress={onCancel}
          >
            <Typography.Label
              regular
              color={StatusColors.Danger}
              css={`
                text-align: center;
              `}
            >
              取消当前部位检测
            </Typography.Label>
          </TouchableOpacity>
        ) : null}
      </ScrollViewContainer>
    );
  },
  ({ route }) =>
    ({
      title: route.params.site.name,
      headerShown: true,
      cardStyle: {
        backgroundColor: '#f5f5f5',
      },
    }) as StackNavigationOptions,
);
